import { supabase, unwrap } from '@/services/supabase/client'
import type {
  AttributeSource,
  AttributeType,
  CatDetail,
  CatNeutered, CatSex,
  CollectionEntry,
  TimelineEntry,
} from '@/types/models'

export async function getCollection(ownerId?: string, limit = 200): Promise<CollectionEntry[]> {
  return unwrap(
    await supabase.rpc('get_collection', { p_owner: ownerId ?? null, p_limit: limit }),
  ) as CollectionEntry[]
}

export async function getCatDetail(catId: string, ownerId?: string): Promise<CatDetail> {
  return unwrap(
    await supabase.rpc('get_cat_detail', { p_cat_id: catId, p_owner: ownerId ?? null }),
  ) as CatDetail
}

export async function getCatTimeline(
  catId: string,
  ownerId?: string,
  limit = 200,
): Promise<TimelineEntry[]> {
  return unwrap(
    await supabase.rpc('get_cat_timeline', {
      p_cat_id: catId,
      p_owner: ownerId ?? null,
      p_limit: limit,
    }),
  ) as TimelineEntry[]
}

export async function updateUserCat(input: {
  userCatId: string
  personalName?: string | null
  clearName?: boolean
  sex?: CatSex
  notes?: string | null
  coverMediaId?: string
  celebratedOn?: string | null
  clearCelebrated?: boolean
  neutered?: CatNeutered
}): Promise<void> {
  unwrap(
    await supabase.rpc('update_user_cat', {
      p_user_cat_id: input.userCatId,
      p_personal_name: input.personalName ?? null,
      p_clear_name: input.clearName ?? false,
      p_sex: input.sex ?? null,
      p_notes: input.notes ?? null,
      p_cover_media_id: input.coverMediaId ?? null,
      p_celebrated_on: input.celebratedOn ?? null,
      p_clear_celebrated: input.clearCelebrated ?? false,
      p_neutered: input.neutered ?? null,
    }),
  )
}

export interface HouseholdCatResult {
  cat_id: string
  user_cat_id: string
  catalog_no: number
}

/** Adds a cat that lives with you: no encounter, no location, no matching. */
export async function createHouseholdCat(input: {
  name?: string | null
  sex?: CatSex
  notes?: string | null
}): Promise<HouseholdCatResult> {
  return unwrap(
    await supabase.rpc('create_household_cat', {
      p_name: input.name ?? null,
      p_sex: input.sex ?? 'unknown',
      p_notes: input.notes ?? null,
    }),
  ) as HouseholdCatResult
}

/**
 * Sharing a house cat needs consent: mutual best friends, and a yes from the
 * person who first registered it. These four calls are that conversation.
 */
/** Register a friend's house cat yourself: they are not here (yet). */
export async function createFriendHomeCat(input: {
  name?: string | null
  sex?: CatSex
  notes?: string | null
  friendLabel?: string | null
}): Promise<HouseholdCatResult> {
  return unwrap(
    await supabase.rpc('create_friend_home_cat', {
      p_name: input.name ?? null,
      p_sex: input.sex ?? 'unknown',
      p_notes: input.notes ?? null,
      p_friend_label: input.friendLabel ?? null,
    }),
  ) as HouseholdCatResult
}

/** "It actually lives with me": a placeholder I registered moves home. */
export async function moveFriendCatHome(catId: string): Promise<void> {
  unwrap(await supabase.rpc('move_friend_cat_home', { p_cat_id: catId }))
}

/** Hand a placeholder to its real owner; they accept from their inbox. */
export async function offerCatTransfer(catId: string, toUserId: string): Promise<string> {
  return unwrap(
    await supabase.rpc('offer_cat_transfer', { p_cat_id: catId, p_to: toUserId }),
  ) as string
}

export async function respondCatTransfer(offerId: string, accept: boolean): Promise<void> {
  unwrap(
    await supabase.rpc('respond_cat_transfer', { p_offer_id: offerId, p_accept: accept }),
  )
}

export async function cancelCatTransfer(offerId: string): Promise<void> {
  unwrap(await supabase.rpc('cancel_cat_transfer', { p_offer_id: offerId }))
}

export interface CatTransferOffer {
  offer_id: string
  cat_id: string
  cat_name: string | null
  cover_media_id: string | null
  from_id: string
  from_username: string
  from_display_name: string
  from_avatar_key: string | null
  created_at: string
}

export async function listCatTransferOffers(): Promise<CatTransferOffer[]> {
  return unwrap(await supabase.rpc('list_cat_transfer_offers', {})) as CatTransferOffer[]
}

/** My own outstanding transfer offer for one cat, if any. */
export async function myTransferOfferFor(catId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('cat_transfer_offers')
    .select('id')
    .eq('cat_id', catId)
    .maybeSingle()
  if (error) return null
  return (data as { id: string } | null)?.id ?? null
}

export interface MutualCloseFriend {
  id: string
  username: string
  display_name: string
  avatar_key: string | null
}

export async function listMutualCloseFriends(): Promise<MutualCloseFriend[]> {
  return unwrap(await supabase.rpc('list_mutual_close_friends', {})) as MutualCloseFriend[]
}

/** Save a mutual best friend's house cat onto my private shelf. Instant. */
export async function addFriendHomeCat(catId: string): Promise<HouseholdCatResult> {
  return unwrap(await supabase.rpc('add_friend_home_cat', { p_cat_id: catId })) as HouseholdCatResult
}

export async function requestHouseholdCat(catId: string): Promise<string> {
  return unwrap(await supabase.rpc('request_household_cat', { p_cat_id: catId })) as string
}

export async function cancelHouseholdCatRequest(requestId: string): Promise<void> {
  unwrap(await supabase.rpc('cancel_household_cat_request', { p_request_id: requestId }))
}

export async function respondHouseholdCatRequest(requestId: string, accept: boolean): Promise<void> {
  unwrap(
    await supabase.rpc('respond_household_cat_request', {
      p_request_id: requestId,
      p_accept: accept,
    }),
  )
}

export interface HouseholdCatRequest {
  request_id: string
  cat_id: string
  cat_name: string | null
  cover_media_id: string | null
  requester_id: string
  requester_username: string
  requester_display_name: string
  requester_avatar_key: string | null
  created_at: string
}

export async function listHouseholdCatRequests(): Promise<HouseholdCatRequest[]> {
  return unwrap(
    await supabase.rpc('list_household_cat_requests', {}),
  ) as HouseholdCatRequest[]
}

/** My own pending request for one cat, if any — to show "solicitud enviada". */
export async function myHouseholdRequestFor(catId: string): Promise<string | null> {
  const { data, error } = await supabase
    .from('household_cat_requests')
    .select('id')
    .eq('cat_id', catId)
    .maybeSingle()
  if (error) return null
  return (data as { id: string } | null)?.id ?? null
}

/** Deleting the cat itself — only its first registrant can, and it disappears
 *  for everyone who shared it. Leaving is deleteUserCat, as always. */
export async function deleteHouseholdCat(catId: string): Promise<void> {
  unwrap(await supabase.rpc('delete_household_cat', { p_cat_id: catId }))
}

/** Pin a place onto every one of MY sightings of this cat that lacks one. */
export async function setMissingSightingLocations(
  catId: string,
  lat: number,
  lng: number,
): Promise<number> {
  return unwrap(
    await supabase.rpc('set_missing_sighting_locations', {
      p_cat_id: catId,
      p_lat: lat,
      p_lng: lng,
    }),
  ) as number
}

/** "It was my house cat all along": my street record converts; my encounters
 *  of it (locations included) are erased, the photographs stay. */
export async function convertToHousehold(catId: string): Promise<number> {
  return unwrap(await supabase.rpc('convert_to_household', { p_cat_id: catId })) as number
}

export async function setCatDefaultName(catId: string, name: string): Promise<void> {
  unwrap(await supabase.rpc('set_cat_default_name', { p_cat_id: catId, p_name: name }))
}

export async function setCatAttribute(input: {
  userCatId: string
  type: AttributeType
  value: string | null
  source?: AttributeSource
  confidence?: number
}): Promise<string | null> {
  return unwrap(
    await supabase.rpc('set_cat_attribute', {
      p_user_cat_id: input.userCatId,
      p_type: input.type,
      p_value: input.value,
      p_source: input.source ?? 'human',
      p_confidence: input.confidence ?? null,
    }),
  ) as string | null
}

export async function deleteCatAttribute(id: string): Promise<void> {
  unwrap(await supabase.rpc('delete_cat_attribute', { p_id: id }))
}

export async function deleteUserCat(userCatId: string): Promise<void> {
  unwrap(await supabase.from('user_cats').delete().eq('id', userCatId))
}


/** The day's cat: Álvaro's drum, anniversaries first, birthdays by right. */
export interface CatOfTheDay {
  cat_id: string
  catalog_no: number
  kind: 'normal' | 'anniversary' | 'birthday'
  name: string | null
  cover_media_id: string | null
  household: boolean
  friend_household: boolean
  first_met_at: string
  days_known: number
  last_seen_at: string | null
  days_since_seen: number | null
  sighting_count: number
  years: number | null
  place: string | null
}

export async function getCatOfTheDay(): Promise<CatOfTheDay | null> {
  return unwrap(await supabase.rpc('get_cat_of_the_day', {})) as CatOfTheDay | null
}
